"use client";

import { useState } from "react";
import { ArrowRight, Loader2, Mail } from "lucide-react";
import { TurnstileWidget } from "@/components/site/TurnstileWidget";

/**
 * Minimal newsletter signup for the global footer.
 * Posts to the existing /api/email-capture endpoint.
 * Dark-themed to match the slate-900 footer background.
 */
export function FooterNewsletterForm() {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!consent) {
      setError("Please tick the consent box to continue.");
      return;
    }
    if (!turnstileToken) {
      setError("Complete the verification challenge to continue.");
      return;
    }
    if (honeypot) {
      setSubmitted(true);
      return;
    }
    setError("");
    setIsPending(true);
    try {
      const res = await fetch("/api/email-capture", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          pageUrl: typeof window !== "undefined" ? window.location.href : "",
          consent: true,
          turnstileToken,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError((data as { error?: string }).error ?? "Something went wrong. Please try again.");
        return;
      }
      setSubmitted(true);
    } catch {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setTurnstileToken("");
      setTurnstileResetKey((current) => current + 1);
      setIsPending(false);
    }
  }

  if (submitted) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-emerald-500/20 bg-white/5 px-4 py-3">
        <Mail size={16} className="text-emerald-400 shrink-0" />
        <p className="text-sm font-semibold text-emerald-300">
          You are on the list. We&apos;ll send pricing updates when they&apos;re ready.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3" noValidate>
      <div className="absolute -left-[9999px]" aria-hidden="true">
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <label htmlFor="footer-newsletter-email" className="sr-only">
            Email address
          </label>
          <input
            id="footer-newsletter-email"
            type="email"
            required
            placeholder="you@example.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError("");
            }}
            aria-invalid={!!error}
            aria-describedby={error ? "footer-newsletter-error" : undefined}
            className="h-11 w-full rounded-xl border border-white/10 bg-white/5 pl-9 pr-3 text-sm font-medium text-slate-100 placeholder:text-slate-500 outline-none focus:border-emerald-600/40 focus:ring-2 focus:ring-emerald-600/10 transition-colors"
          />
        </div>
        <button
          type="submit"
          disabled={isPending || !consent || !turnstileToken}
          className="inline-flex h-11 shrink-0 items-center gap-2 rounded-xl bg-emerald-700 px-5 text-sm font-bold text-white hover:bg-emerald-800 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isPending ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Sending
            </>
          ) : (
            <>
              Subscribe
              <ArrowRight size={14} />
            </>
          )}
        </button>
      </div>

      {error && (
        <p id="footer-newsletter-error" role="alert" className="text-xs font-medium text-rose-400">
          {error}
        </p>
      )}

      <TurnstileWidget
        action="email-capture"
        resetKey={turnstileResetKey}
        onTokenChange={setTurnstileToken}
        className="mt-1 [&_p]:text-slate-400 [&_.text-emerald-700]:text-emerald-400 [&_.text-rose-700]:text-rose-400"
      />

      <label
        htmlFor="footer-newsletter-consent"
        className="flex items-start gap-2 text-xs text-slate-400 cursor-pointer"
      >
        <input
          id="footer-newsletter-consent"
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-emerald-600"
          required
        />
        <span>
          Email me medical cost updates for Australia. I have read the{" "}
          <a href="/privacy" className="underline hover:text-slate-300 transition-colors">
            privacy policy
          </a>{" "}
          and can unsubscribe any time.
        </span>
      </label>
    </form>
  );
}
